// src/types/dashboardTypes.ts
export type DashboardDataType = "metrics" | "percentageChanges" | "ratios" | "current" | "rollups";


export type DpaType =
  | "aggregators"
  | "cex-tokens"
  | "dex"
  | "otherDefi"
  | "perpetuals"
  | "protocolsRwa";

export type Layer12Type = "layer1" | "layer2" | "sideChainsBitcoinL2s";
export type GpsType = "gamingGameFi" | "privacy" | "socialFi";

export type AwType =
  | "ai"
  | "commodities"
  | "globalBonds"
  | "indexingData"
  | "institutionalFunds"
  | "privateCredit"
  | "stakedAssets"
  | "stocks"
  | "usTreasuries"
  | "wrappedAssets";

export type IndividualDashboardType =
  | "dataAvailabilityLayers"
  | "forks"
  | "gpus"
  | "indexingData"
  | "interoperabilityBridges"
  | "identity"
  | "lendingBorrowing"
  | "mapping"
  | "oracles"
  | "protocolsLst"
  | "stakedAssets"
  | "storageFileSharing"
  | "telecom"
  | "yieldFarming";

export type MoDashboardType = "memecoins" | "memecoin-platforms" | "other";
export type StablecoinsMainType = "stablecoins" | "stablecoin-protocols" | "stablecoin-assets" | "stablecoin-yield";

export type DashboardType =
  | DpaType
  | Layer12Type
  | AwType
  | IndividualDashboardType
  | MoDashboardType
  | GpsType
  | StablecoinsMainType;

export interface DashboardConfig {
  type: DashboardType;
  displayName: string;
  description: string;
}

export interface Layer12DashboardConfig {
  type: Layer12Type;
  displayName: string;
  description: string;
}
export const DASHBOARD_CONFIGS: Record<DashboardType, DashboardConfig> = {
  stablecoins: {
    type: "stablecoins",
    displayName: "Stablecoins",
    description: "Stablecoin protocols and assets",
  },
  "stablecoin-protocols": {
    type: "stablecoin-protocols",
    displayName: "Stablecoin Protocols",
    description: "Stablecoin protocol platforms",
  },
  "stablecoin-assets": {
    type: "stablecoin-assets",
    displayName: "Stablecoin Assets",
    description: "Stablecoin assets and tokens",
  },
  "stablecoin-yield": {
    type: "stablecoin-yield",
    displayName: "Yield-bearing Stablecoins",
    description: "Yield-bearing stablecoin protocols",
  },
  aggregators: {
    type: "aggregators",
    displayName: "Aggregators",
    description: "DEX aggregator platforms",
  },
  ai: {
    type: "ai",
    displayName: "AI Tokens",
    description: "AI Tokens platforms",
  },
  "cex-tokens": {
    type: "cex-tokens",
    displayName: "CEX Tokens",
    description: "CEX Tokens platforms",
  },
  commodities: {
    type: "commodities",
    displayName: "Commodities",
    description: "Commodity trading and investment platforms",
  },
  dataAvailabilityLayers: {
    type: "dataAvailabilityLayers",
    displayName: "Data Availability",
    description: "Data availability layer protocols",
  },
  dex: {
    type: "dex",
    displayName: "DEX",
    description: "Decentralized exchanges",
  },
  forks: {
    type: "forks",
    displayName: "Forks",
    description: "Forks of existing blockchain projects and platforms",
  },
  gamingGameFi: {
    type: "gamingGameFi",
    displayName: "Gaming & GameFi",
    description: "Gaming and GameFi platforms",
  },
  globalBonds: {
    type: "globalBonds",
    displayName: "Global Bonds",
    description: "Global bond markets and instruments",
  },
  gpus: {
    type: "gpus",
    displayName: "GPUs",
    description: "GPU trading and investment platforms",
  },
  indexingData: {
    type: "indexingData",
    displayName: "Indexing",
    description: "Data indexing and query protocols",
  },
  identity: {
    type: "identity",
    displayName: "Identity",
    description: "Decentralized identity and DID middleware",
  },
  institutionalFunds: {
    type: "institutionalFunds",
    displayName: "Institutional Funds",
    description: "Institutional investment funds and products",
  },
  interoperabilityBridges: {
    type: "interoperabilityBridges",
    displayName: "Bridges",
    description: "Cross-chain bridge and interoperability protocols",
  },
  layer1: {
    type: "layer1",
    displayName: "Layer 1",
    description: "Layer 1 blockchain networks and protocols",
  },
  layer2: {
    type: "layer2",
    displayName: "Layer 2",
    description: "Layer 2 scaling solutions and networks",
  },
  lendingBorrowing: {
    type: "lendingBorrowing",
    displayName: "Lending & Borrowing",
    description: "Lending and borrowing protocols",
  },
  mapping: {
    type: "mapping",
    displayName: "Mapping",
    description: "Mapping protocols and platforms",
  },
  memecoins: {
    type: "memecoins",
    displayName: "Memecoins",
    description: "Memecoin projects and platforms",
  },
  "memecoin-platforms": {
    type: "memecoin-platforms",
    displayName: "Memecoin Platforms",
    description: "Platforms for creating and trading memecoins",
  },
  oracles: {
    type: "oracles",
    displayName: "Oracles",
    description: "Blockchain oracle networks and protocols",
  },
  other: {
    type: "other",
    displayName: "Other",
    description: "Other blockchain projects and platforms",
  },
  otherDefi: {
    type: "otherDefi",
    displayName: "Other DeFi",
    description: "Other DeFi platforms",
  },
  perpetuals: {
    type: "perpetuals",
    displayName: "Perpetuals",
    description: "Perpetual and derivatives platforms",
  },
  privacy: {
    type: "privacy",
    displayName: "Privacy",
    description: "Privacy-focused platforms and solutions",
  },
  privateCredit: {
    type: "privateCredit",
    displayName: "Private Credit",
    description: "Private credit and lending platforms",
  },
  protocolsLst: {
    type: "protocolsLst",
    displayName: "LST Protocols",
    description: "Liquid staking token protocols",
  },
  protocolsRwa: {
    type: "protocolsRwa",
    displayName: "Protocols RWA",
    description: "Protocols RWA platforms",
  },
  sideChainsBitcoinL2s: {
    type: "sideChainsBitcoinL2s",
    displayName: "Sidechains & Bitcoin L2s",
    description: "Sidechains and Layer 2 solutions for Bitcoin",
  },
  socialFi: {
    type: "socialFi",
    displayName: "SocialFi",
    description: "Social finance platforms and protocols",
  },
  stakedAssets: {
    type: "stakedAssets",
    displayName: "Staked Assets",
    description: "Staked asset protocols and platforms",
  },
  stocks: {
    type: "stocks",
    displayName: "Stocks",
    description: "Stock market and equity platforms",
  },
  storageFileSharing: {
    type: "storageFileSharing",
    displayName: "Storage & File Sharing",
    description: "Decentralized storage and file sharing platforms",
  },
  telecom: {
    type: "telecom",
    displayName: "Telecom",
    description: "Telecom infrastructure and services",
  },
  usTreasuries: {
    type: "usTreasuries",
    displayName: "US Treasuries",
    description: "US Treasury bonds and securities",
  },
  wrappedAssets: {
    type: "wrappedAssets",
    displayName: "Wrapped Assets",
    description: "Wrapped assets platforms",
  },
  yieldFarming: {
    type: "yieldFarming",
    displayName: "Yield Farming",
    description: "Yield farming and liquidity mining protocols",
  },
};
